<template>
  <div class="aiot-platform">
    <!-- 装饰性背景元素 -->
    <div class="background-decorations">
      <div class="floating-particles"></div>
      <div class="geometric-shapes">
        <div class="shape shape-1"></div>
        <div class="shape shape-2"></div>
        <div class="shape shape-3"></div>
        <div class="shape shape-4"></div>
        <div class="shape shape-5"></div>
      </div>
      <div class="gradient-overlay"></div>
    </div>

    <!-- 统一导航栏 -->
    <UnifiedNavBar />

    <!-- 页面头部 -->
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">设备运维平台</h1>
      </div>
    </div>

    <!-- 产品概述 -->
    <section class="product-overview">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">产品概述</h2>
        </div>
        <div class="overview-content">
          <div class="overview-text">
            <p>依托多类型、高精度传感器、边缘计算与AI 诊断算法，打造 “实时监测 - 智能预警 - 闭环处置 - 全周期管理” 的一体化设备运维体系。平台可实时采集设备运行参数，通过边缘节点实现毫秒级数据预处理，结合云端 AI 诊断模型对数据进行深度分析，能精准识别设备异常状态并触发分级告警，助力运维人员快速定位故障点、制定修复方案，可基于设备历史运行数据与行业故障规律，提前预判潜在风险，生成预防性维护计划。</p>
          </div>
          <div class="overview-image">
            <img
              src="/imags/2yunwei/equipment-Product Overview.png"
              class="platform-image"
            />
          </div>
        </div>
      </div>
    </section>
    <!-- 产品功能 -->
    <section class="product-functions">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">产品功能</h2>
        </div>
        <div class="functions-list">
          <div class="function-row">
            <div class="func-text">
              <h3><span class="blue-bar"></span>设备管理</h3>
              <p>
                构建设备全生命周期管理体系，涵盖设备台账建立、设备履历实时更新、设备盘点、设备处置流程、设备技改实施等核心环节，实现设备全周期的规范化、精细化管控。
               </p>
            </div>
            <div class="func-image">
              <img src="/imags/2yunwei/equipment-function1.png"  />
            </div>
          </div>

          <div class="function-row reverse">
            <div class="func-text">
              <h3><span class="blue-bar"></span>预测性智能诊断</h3>
              <p>
                基于机器学习算法和模型分析评估设备健康状况，推送合理的维修或故障诊断报告，依据故障信息知识库解决方案，智能定位并预警设备故障。
              </p>
            </div>
            <div class="func-image">
              <img src="/imags/2yunwei/equipment-function2.png" />
            </div>
          </div>

          <div class="function-row">
            <div class="func-text">
              <h3><span class="blue-bar"></span>设备维修与保养</h3>
              <p>
                支持设备故障、性能异常等多场景报修，可手动填报或自动触发，系统会生成标准化维修工单并匹配最优维修方案；自动编制全周期保养点检计划，并推送至对应执行人员，完成后通过记录上传与校验形成闭环管理，保障维修高效与设备健康。
              </p>
            </div>
            <div class="func-image">
              <img src="/imags/2yunwei/equipment-function3.png" />
            </div>
          </div>

          <div class="function-row reverse">
            <div class="func-text">
              <h3><span class="blue-bar"></span>设备状态监控</h3>
              <p>
                借助 IOT 数据采集技术，实时监测设备运行状态、精准预警设备故障；实现工单预防性维护的协同管理，将设备管理从传统的 “信息化记录” 升级为 “数字化预警与决策”，助力企业提前干预设备潜在问题，提升设备整体运营效率。
              </p>
            </div>
            <div class="func-image">
              <img src="/imags/2yunwei/equipment-function4.png"  />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 产品特点（简洁版） -->
    <section class="simple-features">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">产品特点</h2>
        </div>
        <div class="simple-features-grid">
          <div class="simple-feature-card">
            <div class="simple-feature-icon">
              <svg
                width="36"
                height="36"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
              >
                <circle cx="12" cy="4" r="2" />
                <circle cx="4" cy="12" r="2" />
                <circle cx="20" cy="12" r="2" />
                <circle cx="12" cy="20" r="2" />
                <path d="M12 6v10M6 12h12" />
              </svg>
            </div>
            <h3 class="simple-feature-title">“透明化”设备</h3>
            <p class="simple-feature-desc">
             全类型设备兼容接入、高频采集、多维度数据整合，形成“设备-数据-流程"一体化数据。
            </p>
          </div>

          <div class="simple-feature-card">
            <div class="simple-feature-icon">
              <svg
                width="36"
                height="36"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
              >
                <path d="M7 16a5 5 0 1 1 10 0H7z" />
                <path d="M12 8a4 4 0 0 0-4 4h8a4 4 0 0 0-4-4z" />
                <path d="M5 16h14" />
              </svg>
            </div>
            <h3 class="simple-feature-title">“智能性”预期</h3>
            <p class="simple-feature-desc">
              精准故障预警与诊断，健康与性能预测，动态维护策略生成，将数据转化为决策依据，从根源上减少非计划停机。
            </p>
          </div>

          <div class="simple-feature-card">
            <div class="simple-feature-icon">
              <svg
                width="36"
                height="36"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
              >
                <rect x="3" y="4" width="18" height="14" rx="2" />
                <path d="M7 9h10M7 12h6M7 15h4" />
              </svg>
            </div>
            <h3 class="simple-feature-title">“全流程”管控</h3>
            <p class="simple-feature-desc">
              自动化工单与协同作业，智能巡检与人工排查，备件与成本精细化管控，打通“监测-报警-工单-验收”全流程，实现运维动作的标准化、协同化。
            </p>
          </div>

          <div class="simple-feature-card">
            <div class="simple-feature-icon">
              <svg
                width="36"
                height="36"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
              >
                <path d="M4 6h16v12H4z" />
                <path d="M7 9h10M7 12h8M7 15h6" />
                <path d="M4 18l3 2h10l3-2" />
              </svg>
            </div>
            <h3 class="simple-feature-title">“灵活性”适配</h3>
            <p class="simple-feature-desc">
              多模式部署与低代码扩展，公有云、私有云、混合云等多种部署方案，适配企业数据安全与合规需求。
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- 成功案例（卡片样式） -->
     <section class="application-effects">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">成功案例</h2>
        </div>
        <div class="cases-grid-2col">
          <div class="case-card-wide">
            <div class="case-image">
              <img src="/imags/2yunwei/equipment-case.png"/>
            </div>
            <div class="case-content">
              <div class="case-header">
                <h3 class="case-title">鲁中设备运维平台案例</h3>
              </div>
              <div class="case-desc">
                <p>鲁中设备公司，专注于专业设备制造领域，主要业务覆盖煤炭机械、矿山设备、环保设备等产品，为企业建设设备数字化运维平台，系统功能包括：设备档案管理、3D设备大屏、智能预测性维护等功能。</p>
              </div>
            </div>
          </div>
          
          <div class="case-card-wide">
            <div class="case-image">
              <img src="/imags/2yunwei/equipment-case1.png" />
            </div>
            <div class="case-content">
              <div class="case-header">
                <h3 class="case-title">某设备租赁共享公司案例</h3>
              </div>
              <div class="case-desc">
                <p>设备租赁共享企业。传统的加工模式是每家个体厂各自封闭管理原材料、设备、人员和工艺，这种模式资源高度分散，易造成浪费。因此，亟待需要一种新模式，提高设备及人力资源的利用率。</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>



    <!-- Footer组件 -->
    <Footer />
  </div>
</template>

<script>
import UnifiedNavBar from "@/components/UnifiedNavBar.vue";
import Footer from "@/components/Footer.vue";

export default {
  name: "AiotPlatform",
  components: {
    UnifiedNavBar,
    Footer,
  },
  data() {
    return {
      activeTab: "shandong",
    };
  },
  mounted() {
    this.handleVideoLoad();
  },
  methods: {
    openCaseDetail(type) {
      this.$message({
        message: "案例详情开发中...",
        type: "info",
      });
    },
    handleVideoLoad() {
      this.$nextTick(() => {
        const video = document.querySelector(".hero-video");
        const fallbackBg = document.querySelector(".fallback-background");

        if (video && fallbackBg) {
          video.style.opacity = "0";
          video.style.display = "block";

          video.addEventListener("canplaythrough", () => {
            video
              .play()
              .then(() => {
                fallbackBg.style.display = "none";
                video.style.opacity = "0.7";
              })
              .catch((e) => {
                video.style.display = "none";
              });
          });

          video.addEventListener("error", () => {
            video.style.display = "none";
          });

          setTimeout(() => {
            if (video.readyState < 3) {
              video.style.display = "none";
            }
          }, 5000);
        }
      });
    },
  },
};
</script>

<style scoped>
.aiot-platform {
  min-height: 100vh;
  background: #ffffff;
  color: #333333;
  font-family: "Microsoft YaHei", "PingFang SC", "Hiragino Sans GB", sans-serif;
  overflow-x: hidden;
  position: relative;
}

/* 装饰性背景元素 */
.background-decorations {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: -1;
}

.floating-particles {
  position: absolute;
  width: 100%;
  height: 100%;
  background-image: radial-gradient(
      2px 2px at 20px 30px,
      rgba(25, 118, 210, 0.3),
      transparent
    ),
    radial-gradient(2px 2px at 40px 70px, rgba(25, 118, 210, 0.2), transparent),
    radial-gradient(1px 1px at 90px 40px, rgba(25, 118, 210, 0.4), transparent),
    radial-gradient(1px 1px at 130px 80px, rgba(25, 118, 210, 0.3), transparent),
    radial-gradient(2px 2px at 160px 30px, rgba(25, 118, 210, 0.2), transparent);
  background-repeat: repeat;
  background-size: 200px 100px;
  animation: float 20s ease-in-out infinite;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px) translateX(0px);
  }
  33% {
    transform: translateY(-10px) translateX(10px);
  }
  66% {
    transform: translateY(5px) translateX(-5px);
  }
}

.geometric-shapes {
  position: absolute;
  width: 100%;
  height: 100%;
}

.shape {
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    rgba(25, 118, 210, 0.1),
    rgba(25, 118, 210, 0.05)
  );
  animation: rotate 30s linear infinite;
}

.shape-1 {
  width: 100px;
  height: 100px;
  top: 10%;
  left: 5%;
  animation-delay: 0s;
}

.shape-2 {
  width: 150px;
  height: 150px;
  top: 60%;
  right: 10%;
  animation-delay: -5s;
  border-radius: 30%;
}

.shape-3 {
  width: 80px;
  height: 80px;
  top: 30%;
  right: 20%;
  animation-delay: -10s;
}

.shape-4 {
  width: 120px;
  height: 120px;
  bottom: 20%;
  left: 15%;
  animation-delay: -15s;
  border-radius: 20%;
}

.shape-5 {
  width: 60px;
  height: 60px;
  top: 80%;
  left: 60%;
  animation-delay: -20s;
}

@keyframes rotate {
  0% {
    transform: rotate(0deg) scale(1);
  }
  50% {
    transform: rotate(180deg) scale(1.1);
  }
  100% {
    transform: rotate(360deg) scale(1);
  }
}

.gradient-overlay {
  position: absolute;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    45deg,
    rgba(25, 118, 210, 0.02) 0%,
    transparent 25%,
    transparent 75%,
    rgba(25, 118, 210, 0.02) 100%
  );
}

.page-header {
  background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
  color: white;
  min-height: 120px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 60px;
  text-align: center;
  position: relative;
  overflow: hidden;
}
.page-header::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 100" fill="%23ffffff" opacity="0.1"><polygon points="0,0 1000,0 1000,100 0,80"/></svg>')
    no-repeat bottom;
  background-size: cover;
}
.page-header::after {
  content: "";
  position: absolute;
  top: -50%;
  left: -50%;
  width: 120%;
  height: 120%;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.1) 0%,
    transparent 70%
  );
  animation: rotate 30s linear infinite;
}

.page-title {
  font-size: 2.6rem;
  font-weight: 700;
  margin: 0;
  position: relative;
  z-index: 1;
}

.page-subtitle {
  font-size: 1.2rem;
  color: #666666;
  margin: 0;
  font-weight: 400;
}

/* 通用样式 */
.section-header {
  text-align: center;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 600;
  color: #1976d2;
  margin: 0;
  position: relative;
  display: inline-block;
}

.section-title::after {
  content: "";
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 3px;
  background: #2196f3;
  border-radius: 2px;
}

/* 通用容器样式 */
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* 通用区域样式 */
section {
  padding: 80px 0;
  background: #ffffff;
  position: relative;
}

section:nth-child(even) {
  background: #f8f9fa;
}

.section-subtitle {
  font-size: 1.2rem;
  text-align: center;
  color: #666666;
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

/* 产品概述样式 */
.product-overview {
  padding: 24px 0;
  background: #ffffff;
}

.overview-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.overview-left {
  flex: 1;
  position: relative;
}

.platform-image {
  width: 100%;
  height: 360px;
  border-radius: 12px;
  object-fit: cover;
  box-shadow: 0 8px 30px rgba(33, 150, 243, 0.15);
  transition: all 0.3s ease;
}

.image-overlay {
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(25, 118, 210, 0.9);
  color: white;
  padding: 12px;
  border-radius: 50%;
  backdrop-filter: blur(10px);
}

.overview-right {
  display: none;
}

.overview-text {
  text-align: left;
  margin-bottom: 4px;
}

.overview-text p {
  font-size: 1.2rem;
  line-height: 2.0;
  color: #374151;
  margin: 0;
}

.overview-features {
  display: grid;
  gap: 20px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #2196f3;
  transition: all 0.3s ease;
}

.feature-item:hover {
  background: #e3f2fd;
  transform: translateX(5px);
}

.feature-item svg {
  color: #2196f3;
  flex-shrink: 0;
}

.feature-item span {
  color: #333;
  font-weight: 500;
}

/* 产品展示样式 */
.product-display {
  background: #f8f9fa;
  padding: 40px 0;
}

.display-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 30px;
  margin-top: 50px;
}

@media (max-width: 768px) {
  .display-grid {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 25px;
  }
}

/* 响应式设计 */

@media (max-width: 768px) {
  .product-display {
    padding: 50px 0;
  }

  .section-title {
    font-size: 1.8rem;
  }

  .section-subtitle {
    font-size: 1rem;
  }

  .display-card {
    margin: 0 5px;
  }

  .card-image-container {
    height: 90px;
  }

  .card-content {
    padding: 18px 15px;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .display-card h3 {
    font-size: 1.1rem;
  }

  .card-badge {
    font-size: 0.7rem;
    padding: 3px 10px;
  }

  .display-card p {
    font-size: 0.85rem;
    line-height: 1.5;
  }

  .card-features {
    gap: 5px;
    margin-bottom: 15px;
  }

  .feature-tag {
    font-size: 0.7rem;
    padding: 4px 8px;
  }

  .card-stats {
    gap: 10px;
    margin-top: 15px;
  }

  .stat-item {
    padding: 10px 6px;
  }

  .stat-value {
    font-size: 1rem;
  }

  .stat-label {
    font-size: 0.7rem;
  }
}

/* 产品展示区域样式 */
.product-display {
  background: linear-gradient(135deg, #f8fbff 0%, #ffffff 100%);
  padding: 80px 0;
}

.section-subtitle {
  text-align: center;
  color: #666;
  font-size: 1.1rem;
  margin-top: 10px;
  font-weight: 400;
}

.display-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  margin-top: 50px;
}

.display-card {
  background: #ffffff;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(25, 118, 210, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(25, 118, 210, 0.05);
  position: relative;
}

.display-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 40px rgba(25, 118, 210, 0.15);
  border-color: rgba(25, 118, 210, 0.1);
}

.card-image-container {
  position: relative;
  height: 200px;
  overflow: hidden;
  background: linear-gradient(135deg, #2196f3 0%, #21cbf3 100%);
}

.card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.3s ease;
  opacity: 0.9;
}

.display-card:hover .card-image {
  transform: scale(1.05);
  opacity: 1;
}

.card-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(33, 150, 243, 0.3) 0%,
    rgba(33, 203, 243, 0.3) 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.3s ease;
}

.display-card:hover .card-overlay {
  opacity: 1;
}

.card-content {
  padding: 25px;
  text-align: left;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
}

.display-card h3 {
  font-size: 1.3rem;
  font-weight: 600;
  color: #1976d2;
  margin: 0;
}

.card-badge {
  background: linear-gradient(135deg, #2196f3, #21cbf3);
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.display-card p {
  color: #666;
  line-height: 1.6;
  font-size: 0.95rem;
  margin-bottom: 20px;
}

.card-features {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
}

.feature-tag {
  background: rgba(33, 150, 243, 0.1);
  color: #1976d2;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 500;
  border: 1px solid rgba(33, 150, 243, 0.2);
  transition: all 0.2s ease;
}

.feature-tag:hover {
  background: rgba(33, 150, 243, 0.15);
  transform: translateY(-1px);
}

.card-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-top: 20px;
}

.stat-item {
  text-align: center;
  padding: 15px 10px;
  background: linear-gradient(135deg, #f8fbff 0%, #f0f7ff 100%);
  border-radius: 12px;
  border: 1px solid rgba(33, 150, 243, 0.1);
  transition: all 0.2s ease;
}

.stat-item:hover {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  transform: translateY(-2px);
  border-color: rgba(33, 150, 243, 0.2);
}

.stat-value {
  font-size: 1.2rem;
  font-weight: 700;
  color: #2196f3;
  display: block;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 0.8rem;
  color: #666;
  font-weight: 500;
}

/* 产品特点样式 */
.product-features {
  padding: 80px 0;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

.feature-category {
  margin-bottom: 60px;
}

.category-title {
  font-size: 1.8rem;
  font-weight: 700;
  color: #1976d2;
  text-align: center;
  margin-bottom: 40px;
  position: relative;
  padding-bottom: 15px;
}

.features-grid {
  display: grid;
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto;
}

/* 使用便捷部分统一样式 */
.usage-features-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.usage-feature-card {
  background: white;
  padding: 24px 20px;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(25, 118, 210, 0.08);
  transition: all 0.3s ease;
  border: 1px solid rgba(25, 118, 210, 0.1);
  text-align: center;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
}

.usage-feature-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(25, 118, 210, 0.15);
  border-color: rgba(25, 118, 210, 0.2);
}

.usage-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  flex-shrink: 0;
}

.usage-icon svg {
  color: #1976d2;
  width: 32px;
  height: 32px;
}

.usage-feature-card h4 {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
  line-height: 1.3;
  flex-shrink: 0;
}

.usage-feature-card p {
  color: #64748b;
  line-height: 1.5;
  font-size: 14px;
  flex: 1;
  margin: 0;
}

/* 响应式布局 */
 


@media (max-width: 768px) {
  .usage-features-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .usage-feature-card {
    padding: 24px 20px;
    min-height: 160px;
  }
}

.tech-features {
  grid-template-columns: repeat(3, 1fr);
}

.use-features {
  grid-template-columns: repeat(3, 1fr);
}

 

@media (max-width: 768px) {
  .tech-features,
  .use-features {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

.feature-card {
  background: white;
  padding: 24px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 1px solid rgba(25, 118, 210, 0.1);
  position: relative;
  overflow: hidden;
  min-height: 280px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  text-align: center;
}

.feature-card.large-card {
  min-height: 320px;
  grid-column: span 2;
}

@media (max-width: 768px) {
  .feature-card.large-card {
    grid-column: span 1;
  }
}

.feature-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 40px rgba(25, 118, 210, 0.15);
}

.feature-icon {
  margin-bottom: 16px;
  display: flex;
  justify-content: center;
  flex-shrink: 0;
}

.feature-card h4 {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
  text-align: center;
  line-height: 1.4;
  flex-shrink: 0;
}

.feature-card p {
  color: #64748b;
  line-height: 1.5;
  text-align: center;
  font-size: 13px;
  flex: 1;
  display: flex;
  align-items: flex-start;
}

/* 产品功能样式 */
.product-functions {
  background: #eaf2ff;
  padding: 20px 0;
}

.functions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.5rem;
  margin-top: 50px;
}

.function-item {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1.5rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.function-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(25, 118, 210, 0.1);
  border-color: #1976d2;
}

.function-icon {
  flex-shrink: 0;
  margin-top: 0.2rem;
}

.function-content h3 {
  font-size: 1.4rem;
  font-weight: 600;
  color: #1976d2;
  margin-bottom: 10px;
}

.function-content p {
  color: #666666;
  line-height: 1.6;
}

.functions-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.function-row {
  display: flex;
  align-items: center;
  gap: 100px;
}

.function-row.reverse {
  flex-direction: row-reverse;
}

.func-text {
  flex: 1.2;
}

.func-text h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.blue-bar {
  width: 4px;
  height: 24px;
  background: #3b82f6;
  border-radius: 2px;
  flex-shrink: 0;
}

.func-text p {
  color: #4b5563;
  line-height: 3;
  font-size: 1.08rem;
}

.func-image {
  flex: 1;
  max-width: 400px;
}

.func-image img {
  width: 100%;
  border-radius: 12px;
  box-shadow: none;
  border: none;
}

@media (max-width: 768px) {
  .function-row,
  .function-row.reverse {
    flex-direction: column;
    align-items: stretch;
  }
  .func-image {
    max-width: none;
    width: 100%;
  }
}

/* 功能详细展示样式 */
.function-details {
  margin-top: 60px;
  padding: 40px 0;
  background: linear-gradient(135deg, #f8faff 0%, #ffffff 100%);
  border-radius: 12px;
}

/* 简洁版产品特点模块样式 */
.simple-features {
  padding: 26px 0;
  background: #f3f6fa;
}

.simple-features-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}

.simple-feature-card {
  text-align: center;
  background: #fff;
  border: 1px solid #e6eef7;
  border-radius: 12px;
  padding: 24px 20px;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.simple-feature-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(25, 118, 210, 0.12);
  border-color: #cfe3fb;
}

.simple-feature-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
  color: #1f2937;
  background: linear-gradient(135deg, #f0f7ff 0%, #ffffff 100%);
  border: 1px solid #e6eef7;
}

.simple-feature-title {
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 8px;
}

.simple-feature-desc {
  color: #4b5563;
  font-size: 13px;
  line-height: 1.7;
  margin: 0;
}

 

@media (max-width: 768px) {
  .simple-features-grid {
    grid-template-columns: 1fr;
  }
}

.featured-function {
  background: white;
  border-radius: 12px;
  padding: 30px;
  margin-bottom: 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e3f2fd;
  transition: all 0.3s ease;
}

.featured-function:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(25, 118, 210, 0.12);
}

.featured-function h4 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1976d2;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.featured-function p {
  color: #555;
  line-height: 1.7;
  font-size: 1rem;
}

/* 成功案例 - 重新设计 */
  .application-effects {
    padding: 24px 0;
    background: #eaf2ff;
  }



.case-desc {
  font-size: 14px;
  color: #4b5563;
  line-height: 1.7;
}

.case-footer {
  padding: 12px 16px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #f1f5f9;
  cursor: pointer;
}

.detail-link {
  color: #1f2937;
  font-size: 14px;
  text-decoration: none;
}

.detail-link:hover { color: #2563eb; }

.arrow-circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid #d1d5db;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #374151;
}

 

 

.application-effects::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: none !important;
  pointer-events: none;
  display: none;
}

/* 成功案例简洁版样式 */
  .effects-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
    align-items: stretch;
  }

.effect-card {
  background: #fff;
  border: 1px solid #e6eef7;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.06);
  display: flex;
  flex-direction: column;
  height: 100%;
}

.effect-image img {
  width: 100%;
  border-radius: 10px;
  display: block;
  box-shadow: 0 6px 16px rgba(25, 118, 210, 0.1);
  margin-bottom: 12px;
}

.effect-desc {
  color: #4b5563;
  font-size: 14px;
  line-height: 1.8;
  margin: 0;
  flex: 1;
}

 

@media (max-width: 768px) {
  .effects-grid {
    grid-template-columns: 1fr;
  }
}

/* 重新设计的统计区域 */
.effects-stats-container {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: stretch;
}

.stats-grid-redesigned {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 32px;
  flex: 1;
  align-items: stretch;
}

.stat-card-redesigned {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(15px);
  padding: 36px 24px;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(33, 150, 243, 0.08);
  border: 1px solid rgba(33, 150, 243, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  min-height: 200px;
}

.stat-card-redesigned::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, #2196f3, #21cbf3);
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.stat-card-redesigned:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(33, 150, 243, 0.12);
  border-color: rgba(33, 150, 243, 0.15);
}

.stat-card-redesigned:hover::before {
  transform: scaleX(1);
}

/* 重新设计的图标样式 */
.stat-icon-wrapper-redesigned {
  position: relative;
  width: 64px;
  height: 64px;
  margin: 0 auto 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-icon-bg-redesigned {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(33, 150, 243, 0.08),
    rgba(33, 203, 243, 0.08)
  );
  border-radius: 12px;
  transition: all 0.3s ease;
}

.stat-card-redesigned:hover .stat-icon-bg-redesigned {
  background: linear-gradient(
    135deg,
    rgba(33, 150, 243, 0.15),
    rgba(33, 203, 243, 0.15)
  );
  transform: scale(1.05);
}

.stat-icon-redesigned {
  position: relative;
  z-index: 1;
  color: #2196f3;
  font-size: 24px;
  transition: all 0.3s ease;
}

.stat-card-redesigned:hover .stat-icon-redesigned {
  color: #1976d2;
  transform: scale(1.1);
}

/* 重新设计的统计内容 */
.stat-content-redesigned {
  text-align: center;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.stat-number-redesigned {
  font-size: 36px;
  font-weight: 700;
  color: #2196f3;
  line-height: 1;
  display: inline-block;
  margin-bottom: 8px;
}

.stat-suffix-redesigned {
  font-size: 24px;
  font-weight: 600;
  color: #2196f3;
  margin-left: 2px;
}

.stat-label-redesigned {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
}

.stat-desc-redesigned {
  font-size: 12px;
  color: #666;
  margin-bottom: 12px;
  line-height: 1.3;
}

/* 重新设计的进度条 */
.stat-progress-redesigned {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
}

.progress-track-redesigned {
  flex: 1;
  height: 4px;
  background: rgba(33, 150, 243, 0.08);
  border-radius: 2px;
  overflow: hidden;
  position: relative;
}

.progress-fill-redesigned {
  height: 100%;
  background: linear-gradient(90deg, #2196f3, #21cbf3);
  border-radius: 2px;
  transition: width 1s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.progress-fill-redesigned::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.4),
    transparent
  );
  animation: shimmer-redesigned 2s infinite;
}

@keyframes shimmer-redesigned {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.progress-text-redesigned {
  font-size: 11px;
  font-weight: 600;
  color: #2196f3;
  min-width: 28px;
}

/* 重新设计的可视化区域 */
.effects-visualization-container {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.chart-display-area {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(15px);
  border-radius: 20px;
  padding: 32px;
  box-shadow: 0 8px 32px rgba(33, 150, 243, 0.08);
  border: 1px solid rgba(33, 150, 243, 0.06);
  transition: all 0.3s ease;
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 600px;
}

.chart-display-area:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(33, 150, 243, 0.12);
}

/* 重新设计的图表头部 */
.chart-header-redesigned {
  text-align: center;
  margin-bottom: 24px;
}

.chart-title-redesigned {
  font-size: 22px;
  font-weight: 700;
  color: #333;
  margin-bottom: 8px;
}

.chart-subtitle-redesigned {
  font-size: 14px;
  color: #666;
  opacity: 0.8;
}

.chart-main-redesigned {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.chart-image-container-redesigned {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 24px;
  flex: 1;
  max-height: 320px;
}

.chart-image-redesigned {
  width: 100%;
  height: 280px;
  object-fit: cover;
  display: block;
  transition: transform 0.3s ease;
}

.chart-display-area:hover .chart-image-redesigned {
  transform: scale(1.01);
}

/* 重新设计的图表覆盖层 */
.chart-overlay-gradient-redesigned {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(33, 150, 243, 0.05) 0%,
    transparent 50%,
    rgba(33, 203, 243, 0.05) 100%
  );
  opacity: 0;
  transition: opacity 0.3s ease;
}

.chart-display-area:hover .chart-overlay-gradient-redesigned {
  opacity: 1;
}

.chart-metrics-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

/* 重新设计的图表统计项 */
.chart-metric-item {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  padding: 16px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 4px 16px rgba(33, 150, 243, 0.08);
  border: 1px solid rgba(33, 150, 243, 0.08);
  transition: all 0.3s ease;
}

.chart-metric-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(33, 150, 243, 0.1);
}

.chart-metric-icon {
  width: 36px;
  height: 36px;
  background: linear-gradient(
    135deg,
    rgba(33, 150, 243, 0.1),
    rgba(33, 203, 243, 0.1)
  );
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2196f3;
  flex-shrink: 0;
  font-size: 16px;
}

.chart-metric-content {
  flex: 1;
  min-width: 0;
}

.chart-metric-value {
  font-size: 18px;
  font-weight: 700;
  color: #2196f3;
  line-height: 1.2;
}

.chart-metric-label {
  font-size: 11px;
  color: #666;
  margin-top: 1px;
}

/* 重新设计的成功案例区域 */
.effects-partnership-section {
  max-width: 1200px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(15px);
  border-radius: 24px;
  box-shadow: 0 12px 40px rgba(33, 150, 243, 0.1);
  border: 1px solid rgba(33, 150, 243, 0.08);
  transition: all 0.3s ease;
}

/* 成功案例列表样式 */
.effects-list {
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-top: 8px;
}

.effect-item {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding: 24px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 16px;
  border: 1px solid rgba(33, 150, 243, 0.08);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.effect-item::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(135deg, #2196f3, #21cbf3);
  transition: all 0.3s ease;
}

.effect-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 32px rgba(33, 150, 243, 0.12);
  background: rgba(255, 255, 255, 0.8);
}

.effect-item:hover::before {
  width: 6px;
}

.effect-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #2196f3, #21cbf3);
  color: white;
  font-size: 18px;
  font-weight: 700;
  border-radius: 12px;
  flex-shrink: 0;
  box-shadow: 0 4px 16px rgba(33, 150, 243, 0.3);
  transition: all 0.3s ease;
}

.effect-item:hover .effect-number {
  transform: scale(1.05);
  box-shadow: 0 6px 20px rgba(33, 150, 243, 0.4);
}

.effect-content {
  flex: 1;
}

.effect-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  line-height: 1.4;
}

.effect-description {
  font-size: 15px;
  line-height: 1.6;
  color: #666;
  margin: 0;
}

/* 响应式设计 */
 

 


@media (max-width: 768px) {
  .application-effects {
    padding: 40px 0;
  }

  .section-title {
    font-size: 28px;
  }

  .section-subtitle {
    font-size: 14px;
  }

  .effects-main-content {
    margin-top: 30px;
    gap: 30px;
  }

  .stat-card-redesigned {
    padding: 16px 12px;
  }

  .stat-icon-wrapper-redesigned {
    width: 48px;
    height: 48px;
    margin-bottom: 12px;
  }

  .stat-number-redesigned {
    font-size: 24px;
  }

  .stat-suffix-redesigned {
    font-size: 18px;
  }

  .stat-label-redesigned {
    font-size: 14px;
  }

  .stat-description-redesigned {
    font-size: 12px;
  }

  .chart-display-area {
    padding: 16px;
  }

  .chart-title-redesigned {
    font-size: 18px;
  }

  .chart-subtitle-redesigned {
    font-size: 12px;
  }

  .chart-metric-value {
    font-size: 14px;
  }

  .chart-metric-label {
    font-size: 11px;
  }

  .effects-partnership-section {
    margin-top: 60px;
    padding: 20px;
  }
}

.case-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.case-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #2c3e50;
}

.case-industry {
  background: #0052cc;
  color: white;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
}

.case-desc {
  color: #666;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.case-results {
  display: flex;
  justify-content: space-between;
}

.result-item {
  text-align: center;
}

.result-value {
  display: block;
  font-size: 1.3rem;
  font-weight: 700;
  color: #0052cc;
}

.result-metric {
  font-size: 0.8rem;
  color: #666;
}


/* 通用样式 */
.section-title {
  font-size: 2.5rem;
  font-weight: 600;
  color: #2c3e50;
  text-align: center;
  margin-bottom: 20px;
}

.section-subtitle {
  font-size: 1.1rem;
  color: #666666;
  text-align: center;
  margin-bottom: 50px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

 

@media (max-width: 768px) {
  .page-header {
    padding: 48px 0 36px;
  }
  .page-title {
    font-size: 1.9rem;
  }

  .section-title {
    font-size: 1.8rem;
  }

  .display-item {
    padding: 20px;
  }

  .feature-card {
    padding: 20px;
  }

  .feature-icon {
    font-size: 2.5rem;
  }

  .effects-content {
    grid-template-columns: 1fr;
  }

  .effects-list {
    gap: 20px;
  }

  .effect-item {
    padding: 16px;
    gap: 16px;
    flex-direction: column;
    text-align: center;
  }

  .effect-number {
    width: 40px;
    height: 40px;
    font-size: 14px;
    align-self: center;
  }

  .effect-title {
    font-size: 16px;
  }

  .effect-description {
    font-size: 14px;
  }

  .case-stats {
    grid-template-columns: 1fr;
  }

  .case-stat-value {
    font-size: 2rem;
  }
}
/* 简洁版产品特点重构覆盖样式 */
.product-features {
  padding: 60px 0;
  background: #ffffff;
}

.category-title {
  font-size: 20px;
  font-weight: 600;
  color: #1976d2;
  text-align: center;
  margin-bottom: 24px;
}

.features-grid {
  display: grid;
  gap: 16px;
}

/* 桌面端布局 */
.tech-grid {
  grid-template-columns: repeat(3, 1fr);
}

.usage-grid.simple-usage-grid {
  grid-template-columns: repeat(5, 1fr);
}

/* 平板端 */
 

/* 手机端 */
@media (max-width: 768px) {
  .tech-grid,
  .usage-grid.simple-usage-grid {
    grid-template-columns: 1fr;
  }
}

/* 卡片简化样式 */
.feature-card {
  background: #fff;
  border: 1px solid #e6eef7;
  border-radius: 12px;
  padding: 20px;
  min-height: 240px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  text-align: center;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.06);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.feature-card::before {
  display: none;
}

.feature-card:hover {
  box-shadow: 0 6px 16px rgba(25, 118, 210, 0.1);
  border-color: #cfe3fb;
  transform: none;
}

.simple-card h4 {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 10px 0 8px;
}

.simple-card p {
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
}

.simple-icon {
  color: #1976d2;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 48px;
  height: 48px;
}

/* 亮点标签样式 */
.feature-highlights {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-top: 10px;
}

.highlight-item {
  padding: 4px 8px;
  font-size: 12px;
  color: #1976d2;
  background: #f3f8fe;
  border: 1px solid #e6eef7;
  border-radius: 999px;
}



/* 案例卡片网格样式 - 已使用新的cases-grid-2col */



.case-header {
  display: flex;
  align-items: flex-start;
  margin-bottom: 20px;
}

.case-icon {
  color: #1976d2;
  margin-right: 15px;
  flex-shrink: 0;
  margin-top: 5px;
}

.case-title {
  font-size: 1.3rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.4;
}

.case-content {
  font-size: 1rem;
  line-height: 1.7;
  color: #64748b;
  margin: 0;
}

/* 成功案例2列布局 */
.cases-grid-2col {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 40px;
  margin-top: 40px;
}

.case-card-wide {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid #f1f5f9;
}

.case-card-wide:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

.case-card-wide .case-image {
  height: 200px;
  overflow: hidden;
}

.case-card-wide .case-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.case-card-wide:hover .case-image img {
  transform: scale(1.05);
}

.case-card-wide .case-content {
  padding: 30px;
}

.case-card-wide .case-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.case-card-wide .case-title {
  font-size: 1.4rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  flex: 1;
  margin-right: 15px;
}

.case-card-wide .case-tag {
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  white-space: nowrap;
}

.case-card-wide .case-desc {
  margin-bottom: 25px;
}

.case-card-wide .case-desc p {
  color: #64748b;
  line-height: 1.7;
  margin-bottom: 12px;
  font-size: 1rem;
}

.case-card-wide .case-desc p:last-child {
  margin-bottom: 0;
}

.case-card-wide .case-results {
  display: flex;
  gap: 25px;
  margin-bottom: 25px;
  padding: 20px;
  background: linear-gradient(135deg, #f8fafc, #e2e8f0);
  border-radius: 12px;
}

.case-card-wide .result-item {
  text-align: center;
  flex: 1;
}

.case-card-wide .result-value {
  display: block;
  font-size: 1.8rem;
  font-weight: 700;
  color: #1d4ed8;
  margin-bottom: 5px;
}

.case-card-wide .result-label {
  font-size: 0.9rem;
  color: #64748b;
  font-weight: 500;
}

.case-card-wide .case-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;
}

.case-card-wide .detail-link {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
  font-size: 1rem;
  transition: color 0.3s ease;
}

.case-card-wide .detail-link:hover {
  color: #1d4ed8;
}

.case-card-wide .arrow-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  font-weight: bold;
  transition: transform 0.3s ease;
}

.case-card-wide:hover .arrow-circle {
  transform: translateX(5px);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .cases-grid-2col {
    grid-template-columns: 1fr;
    gap: 30px;
  }
  
  .case-card-wide .case-content {
    padding: 25px;
  }
  
  .case-card-wide .case-title {
    font-size: 1.3rem;
  }
}
</style>
